import { useEstimate } from "@/hooks/studio-platform";
import { cn } from "@/lib/studio/utils";

export interface EstimateBadgeProps {
  skill_id: string;
  className?: string;
}

function formatSeconds(ms: number | undefined): string {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return "?s";
  const s = Math.max(1, Math.round(ms / 1000));
  return `${s}s`;
}

function formatCost(cost: number | undefined): string {
  if (!cost || !Number.isFinite(cost) || cost <= 0) return "free";
  return `$${cost.toFixed(4)}`;
}

/**
 * Inline terminal-flavored badge that summarises the historical cost/latency
 * for a skill.  Hidden entirely on error so it does not clutter the UI.
 */
export function EstimateBadge({ skill_id, className }: EstimateBadgeProps) {
  const { data, isLoading, isError } = useEstimate(skill_id);

  if (isError) return null;

  if (isLoading) {
    return (
      <span
        className={cn(
          "inline-block h-4 w-32 animate-pulse rounded bg-muted/70",
          className
        )}
        aria-hidden="true"
      />
    );
  }

  if (!data) return null;

  const hasHistory = (data.recent_run_count ?? 0) > 0;
  const seconds = formatSeconds(data.est_latency_ms_p50);
  const cost = formatCost(data.est_cost_usd);
  const count = data.recent_run_count;

  const text = hasHistory
    ? `~${seconds} · ${cost} · ${count} run${count === 1 ? "" : "s"}`
    : `~est. ${seconds} · heuristic`;

  const tooltip = hasHistory
    ? `p50 ${formatSeconds(data.est_latency_ms_p50)} · p95 ${formatSeconds(
        data.est_latency_ms_p95
      )} · ${count} recent runs · source: ${data.source}`
    : `heuristic estimate · source: ${data.source}`;

  return (
    <span
      title={tooltip}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 font-mono text-[11px] text-muted-foreground",
        className
      )}
    >
      <span className="text-[hsl(var(--accent-warm))]">$</span>
      {text}
    </span>
  );
}
